import React from 'react';

import { Box } from '@mui/material';
import {
  amber,
  blue,
  cyan,
  deepOrange,
  deepPurple,
  green,
  indigo,
  lime,
  orange,
  purple,
  red,
  teal,
  yellow,
} from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';
import AvatarComponent from 'react-avatar';


import type { AvatarProperties, ColorType } from './Avatar.types';

const baseColors = [
  red,
  green,
  blue,
  amber,
  cyan,
  deepOrange,
  lime,
  deepPurple,
  yellow,
  teal,
  indigo,
  orange,
  purple,
];

const baseVariants = [
  200,
  300,
  400,
  500,
  600,
  700,
  800,
  900,
  'A100',
  'A200',
  'A400',
  'A700',
] as ColorType[];

const colors = baseColors.flatMap(color =>
  baseVariants.map(variant => color[variant]),
);

export const Avatar: React.FC<AvatarProperties> = ({
  fullName,
  email,
  size,
  round,
  children,
  style,
  textSizeRatio,
  textMarginRatio,
  onClick,
  dataTest = 'avatar',
}) => {
  const theme = useTheme();

  const { bgColor, fgColor } = React.useMemo(() => {
    const identification = fullName || email;
    /*
     * `codePointAt(0)` объявлен как `number | undefined` — `undefined` он
     * возвращает только для индекса за пределами строки. Здесь такого индекса
     * нет: спред строки даёт непустые элементы (по одной кодовой точке), а сама
     * строка непуста из-за `|| 'NoName'`. Поэтому здесь приведение, а не `?? 0`:
     * фолбэк был недостижим и прятал ветвь, приведение же фиксирует инвариант
     * на месте — так же, как `number.value() as number` в `NumeralField`.
     */
    const stringHashedAsNumber = [...(identification || 'NoName')].reduce(
      (accum, value) => (value.codePointAt(0) as number) + accum,
      identification?.length || 0,
    );

    const backgroundColor = colors[stringHashedAsNumber % colors.length];
          /*
       * Цвет текста считает тема, а не своя функция (AF-44).
       *
       * Раньше здесь был `getContrastYiq` — выбор чёрного или белого по
       * яркости YIQ. YIQ и WCAG расходятся: на списке из 156 цветов аватара
       * (13 палитр MUI × 12 оттенков) они дают разный ответ в 23 случаях, и
       * во всех двадцати трёх YIQ выбирает белый с контрастом ниже 4.5 —
       * от 2.24:1 до 4.56:1, тогда как тёмный дал бы 4.6–8.1. Худший случай
       * `#00c853`: белый 2.24:1 практически нечитаем.
       *
       * `getContrastText` берёт порог из темы (`contrastThreshold: 4.5`),
       * то есть согласован с остальной палитрой и переживёт смену цветов.
       * Список 23 цветов и метод замера — docs/architecture/design-system.md.
       */
      const foregroundColor = theme.palette.getContrastText(backgroundColor);

    return { bgColor: backgroundColor, fgColor: foregroundColor };
  }, [email, fullName, theme]);

  return (
    <Box data-testid={dataTest} sx={{
      display: 'flex'
    }}>
      <AvatarComponent
        size={size}
        round={round}
        name={fullName}
        email={email}
        style={style}
        textSizeRatio={textSizeRatio}
        textMarginRatio={textMarginRatio}
        color={bgColor}
        fgColor={fgColor}
        onClick={onClick}
      />
      {children}
    </Box>
  );
};
